import axiosClient from "../../../shared/lib/http/axiosClient";
import { Car, CarAdd, CarEdit, CarSchema, CarSearch, CarsPaginatedSchema, CarsResponse } from "@models/car";
import { buildSpatieSort } from "../utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../utils/normalizeCustomDataFilter";


type CarInclude = "customData" | "customer" | "customer.customData";

export type CarSortField =
  | "id"
  | "license_number"
  | "account_number"
  | "info"
  | "manufacturer"
  | "model"
  | "production_year"
  | "created_at"
  | "updated_at";

export type CarSort = CarSortField | `-${CarSortField}`;

const CAR_SORT_FIELDS: CarSortField[] = [
  "id",
  "license_number",
  "account_number",
  "info",
  "manufacturer",
  "model",
  "production_year",
  "created_at",
  "updated_at",
];

const CAR_BROWSE_INCLUDES: CarInclude[] = ["customData", "customer.customData"];
const CAR_BROWSE_PER_PAGE = 10;

export interface CarSearchParams {
  filter?: Partial<CarSearch>;
  include?: CarInclude[];
  sort?: CarSort[];
  per_page?: number;
  cursor?: string;
}

function normalizeCarSearchFilter(filter: CarSearch): CarSearch {
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;
  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

function getSearchBody(params: CarSearchParams) {
  const { filter: rawFilter, include, sort, per_page = CAR_BROWSE_PER_PAGE, cursor } = params;
  const filter = rawFilter ? normalizeCarSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
  };
}

export const buildCarSort = (sortKey: string, order: "ASC" | "DESC"): CarSort =>
  buildSpatieSort(sortKey, order, CAR_SORT_FIELDS, "id") as CarSort;

export const searchCars = async (
  params: CarSearchParams = {},
  signal?: AbortSignal,
): Promise<CarsResponse> => {
  const response = await axiosClient.post(
    "/api/cars/search",
    getSearchBody({
      include: CAR_BROWSE_INCLUDES,
      per_page: CAR_BROWSE_PER_PAGE,
      ...params,
    }),
    { signal },
  );

  return CarsPaginatedSchema.parse(response.data);
};

export const getCarById = async (id: string, signal?: AbortSignal): Promise<Car> => {

  const response = await axiosClient.get(
    `/api/cars/${id}`,
    { signal },
  );

  return response.data;
};


export const addCar = async (carToAdd: CarAdd): Promise<Car> => {

  const response = await axiosClient.post(
    "/api/cars",
    carToAdd,
  );

  return response.data;
};


export const saveCar = async (carId: number, carToSave: CarEdit): Promise<Car> => {

  const response = await axiosClient.patch(
    `/api/cars/${carId}`,
    carToSave,
  );

  return CarSchema.parse(response.data);
};


export const getNextCarNumber = async (): Promise<number> => {

  const response = await axiosClient.get(
    "/api/cars/next-number",
  );

  return response.data.next_car_index;
};


export const deleteCar = async (carId: number): Promise<void> => {

  await axiosClient.delete(
    `/api/cars/${carId}`,
  );
};
